import {AppRoutes} from '../enums/routes';

/**
 * The routes that must render **without** the application chrome — the navbar, the context pill (bottom left),
 * the impersonation banner and the migration/sign-in notices.
 *
 * These are the pages of the *authentication* flow itself, and they are reached in two states that the shell
 * cannot tell apart on its own:
 *
 * - **By somebody who is not the signed-in user.** An invitation link is forwarded and opened on a shared or
 *   borrowed machine. `AppComponent` decides the chrome purely from "is a session stored in this browser", so
 *   the invitee was shown a stranger's tenant, name and navigation — a leak of who last used that browser, on
 *   the one page whose whole point is that the visitor may be a different person.
 * - **By somebody whose session is being REPLACED right now.** `/authentication/signing-in` is the landing of
 *   the external sign-in handoff. The provider callback that produced its URL has already rotated the
 *   `CM-Secret` cookie server-side, so the session still sitting in `localStorage` is *provably* superseded:
 *   its bearer is encrypted under the previous secret and can no longer be decrypted. Rendering the chrome
 *   there fires the shell's own bootstrap calls (`/auth/context/available`, `/auth/migration/status`) with that
 *   dead token, both of which 401 — and `ErrorInterceptor` turns a 401 into a logout.
 *
 * Both are the same defect: the shell is chosen by session state where it should be chosen by route. Keeping
 * the list here rather than in `AppComponent` is what lets `ErrorInterceptor` agree with it (see
 * {@link isExternalSignInLandingPath}) instead of growing a second, drifting copy.
 *
 * **Not** in this list: `/authentication/choose-context`. It is guarded, it always has a real session, and the
 * identity the chrome names there is genuinely the visitor's own.
 */
export const CHROME_FREE_ROUTES: readonly AppRoutes[] = [
  AppRoutes.Login,
  AppRoutes.AuthenticationSigningIn,
  AppRoutes.Invite
];

/**
 * Normalises a `location.pathname` for comparison: query/hash are never part of it, but a trailing slash is,
 * and the router will happily produce one.
 */
function normalise(pathname: string): string {
  const path = pathname.split(/[?#]/)[0];
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
}

/**
 * Whether this path is one of {@link CHROME_FREE_ROUTES}.
 *
 * Matched **exactly**, never with `endsWith`: `AppRoutes.EmployeePanelLogin` is `ep/login`, so a suffix match
 * would strip the chrome off the employee panel's login screen as well — a route that is not in this family and
 * whose shell is load-bearing.
 */
export function isChromeFreePath(pathname: string): boolean {
  const path = normalise(pathname);
  return CHROME_FREE_ROUTES.some(route => path === `/${route}`);
}

/**
 * Whether this path is the external sign-in handoff landing — the one page on which a 401 means "the session
 * that was stored here has been superseded", not "your session died".
 *
 * Read from `location.pathname` rather than the router: the requests this has to cover are fired during app
 * bootstrap, before the first `NavigationEnd`.
 */
export function isExternalSignInLandingPath(pathname: string): boolean {
  return normalise(pathname) === `/${AppRoutes.AuthenticationSigningIn}`;
}
